import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  Typography,
  Button,
  IconButton,
  useTheme,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Snooze } from '@mui/icons-material';
import { DueDate } from '../../utils/dueDates';

interface DueDatePopupProps {
  notification: DueDate;
  open: boolean;
  onClose: () => void;
  onMarkComplete: (id: string) => void;
  onRemindLater: (id: string) => void;
}

const DueDatePopup: React.FC<DueDatePopupProps> = ({
  notification,
  open,
  onClose,
  onMarkComplete,
  onRemindLater,
}) => {
  const theme = useTheme();
  const [isRinging, setIsRinging] = useState(false);

  useEffect(() => {
    if (open) {
      setIsRinging(true);
      const audio = new Audio('/notification-bell.mp3');
      audio.loop = true;
      audio.play();

      const timer = setTimeout(() => {
        audio.pause();
        setIsRinging(false);
      }, 10000);

      return () => {
        audio.pause();
        clearTimeout(timer);
      };
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <DialogContent sx={{ p: 4, color: 'white' }}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            textAlign="center"
            gap={2}
          >
            <motion.div
              animate={isRinging ? { rotate: [0, -10, 10, -10, 0] } : {}}
              transition={{ duration: 0.5, repeat: isRinging ? Infinity : 0 }}
            >
              <IconButton
                size="large"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                }}
              >
                <CheckCircle fontSize="large" />
              </IconButton>
            </motion.div>

            <Typography variant="h5" fontWeight="bold">
              {notification.title}
            </Typography>

            <Typography variant="body1">
              {notification.description}
            </Typography>

            {notification.amount && (
              <Typography variant="h6" fontWeight="bold">
                Amount: ₹{notification.amount.toLocaleString('en-IN')}
              </Typography>
            )}

            <Typography variant="body2">
              Due: {notification.dueDate.toLocaleDateString()}
            </Typography>

            <Box display="flex" gap={2} mt={3}>
              <Button
                variant="contained"
                startIcon={<CheckCircle />}
                onClick={() => onMarkComplete(notification.id)}
                sx={{
                  bgcolor: 'white',
                  color: theme.palette.primary.main,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                }}
              >
                Mark as Complete
              </Button>
              <Button
                variant="outlined"
                startIcon={<Snooze />}
                onClick={() => onRemindLater(notification.id)}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                Remind Me Later
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </motion.div>
    </Dialog>
  );
};

export default DueDatePopup; 